import { useState } from 'react';
import { NPCS, NPCS_BY_ID } from '../data';
import { useGame } from '../state/useGame';
import { hasFlag } from '../domain/quests/engine';
import { harrowRecordedResult, type HarrowResultKey } from '../domain/quests/dialogue';

type Npc = (typeof NPCS.npcs)[number];
type ParliamentNpc = Npc & {
  portrait: string;
  motto: string;
  parliamentSeat: NonNullable<Npc['parliamentSeat']>;
};

function isParliamentNpc(npc: Npc): npc is ParliamentNpc {
  return npc.parliamentSeat !== null && npc.portrait !== null && npc.motto !== null;
}

const SEATS = NPCS.npcs
  .filter(isParliamentNpc)
  .sort((left, right) => left.parliamentSeat.order - right.parliamentSeat.order);

/**
 * Harrow's one real conversation. An inline accessible card (no modal, no
 * focus trap needed): Speak toggles the prompt, the two choices are plain
 * ≥44px buttons, and the recorded result renders on every revisit.
 */
function HarrowConversation() {
  const state = useGame((s) => s.state);
  const speakToHarrow = useGame((s) => s.speakToHarrow);
  const [open, setOpen] = useState(false);
  const [sessionResult, setSessionResult] = useState<HarrowResultKey | null>(null);

  const harrow = NPCS_BY_ID.get('harrow')!;
  const dialogues = harrow.dialogues;
  const recorded = harrowRecordedResult(state);
  const shownResult = sessionResult ?? recorded;

  const choose = (choice: 'job-table' | 'trust') => {
    setSessionResult(speakToHarrow(choice));
  };

  return (
    <div data-npc-id="harrow" style={{ marginTop: 10, width: '100%' }}>
      <button
        className="primary"
        aria-label="Speak to Harrow"
        aria-expanded={open}
        aria-controls="harrow-conversation"
        onClick={() => setOpen((v) => !v)}
      >
        Speak
      </button>
      <div id="harrow-conversation">
        {open && (
          <>
            <p data-dialogue-id="harrow-prompt" style={{ fontSize: 13, marginTop: 10 }}>
              {dialogues.branch_prompt}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button data-dialogue-choice="job-table" onClick={() => choose('job-table')}>
                {dialogues.branch_a_label}
              </button>
              <button data-dialogue-choice="trust" onClick={() => choose('trust')}>
                {dialogues.branch_b_label}
              </button>
            </div>
          </>
        )}
        {shownResult && (
          <p
            data-dialogue-id="harrow-result"
            data-dialogue-result={shownResult}
            aria-live="polite"
            style={{ fontSize: 13, marginTop: 10, fontStyle: 'italic' }}
          >
            {dialogues[shownResult]}
          </p>
        )}
      </div>
    </div>
  );
}

export function ParliamentPage() {
  const state = useGame((s) => s.state);
  const hatchArmed = hasFlag(state, 'hatch_mode');

  return (
    <>
      <h1 className="page-title">Parliament</h1>
      <p className="page-subtitle">
        Four seats. Crown authority stays human-only. HATCH MODE triggers a fifth
        mirror voice from the peer agent — used sparingly.
      </p>

      <div className="parliament">
        {SEATS.map((s) => (
          <div className="seat" key={s.id} data-npc-id={s.id}>
            <img
              src={`/assets/icons/${s.portrait}`}
              alt={`${s.name} portrait`}
              width={128}
              height={128}
              loading="lazy"
              style={{ borderRadius: 8, imageRendering: 'pixelated', background: '#1a1d24' }}
            />
            <h2 style={{ margin: '8px 0 4px', color: 'var(--accent-nous)' }}>{s.name}</h2>
            <div style={{ color: 'var(--ui-text-dim)', fontSize: 12 }}>
              {s.parliamentSeat.displayRole}
            </div>
            <p style={{ marginTop: 12, fontStyle: 'italic' }}>"{s.motto}"</p>
            {s.id === 'harrow' && <HarrowConversation />}
          </div>
        ))}
      </div>

      <div className="card" style={{ marginTop: 18 }} data-hatch-mode={hatchArmed}>
        <strong>HATCH MODE</strong>
        {hatchArmed ? (
          <p style={{ margin: '6px 0 0', color: 'var(--ui-text-dim)', fontSize: 13 }}>
            HATCH MODE armed. The Ship It feather is filed and the flag is recorded on
            your save — an honest flag, nothing more. The dual-agent mirror voice
            itself is a later phase; tonight the parakeet is simply quieter.
          </p>
        ) : (
          <p style={{ margin: '6px 0 0', color: 'var(--ui-text-dim)', fontSize: 13 }}>
            Optional dual-agent stance. Triggers when Mr. Sparks commissions a second
            agent (e.g. peer-routing into Sagikos's session). Complete the Crown quest
            <em> Ship It</em> to arm the flag.
          </p>
        )}
      </div>
    </>
  );
}
